import React from "react";
import styled from "styled-components";
import { useTranslation } from "next-i18next";
import { ProductCard, ProductCardProps } from "./ProductCard";
import { ProductFilter } from "./ProductFilter";

export interface ProductCardListProps {
  products: ProductCardProps[];
  title?: string;
}

const MainContainer = styled.div`
  background: linear-gradient(180deg, #ecefff 0%, #ffffff 100%);
  border-top: 1.5px solid #d9dbde;
  padding: 40px 16px 200px 16px;
  width: 100%;
  box-sizing: border-box;
  margin: 1px 0;
`;

const Headline = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0;
  gap: 5px;
  width: 100%;
  max-width: 100%;
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 600;
  line-height: 36px;
  display: flex;
  align-items: center;
  color: #202123;
  margin: 0;
`;

const Subtitle = styled.p`
  font-style: normal;
  font-weight: 400;
  font-size: 15px;
  line-height: 24px;
  display: flex;
  align-items: center;
  color: #5f6468;
  margin: 0;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  max-width: 1412px;
  margin: 0 auto;

  @media (max-width: 1440px) {
    max-width: 1053px;
  }

  @media (max-width: 1080px) {
    max-width: 694px;
  }

  @media (max-width: 720px) {
    max-width: 335px;
  }
`;

const GridWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  width: 100%;
  justify-content: center;
  justify-items: center;

  @media (max-width: 1440px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 1080px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const val: any = () => null;
val.isRequired = () => null;

export const ProductCardList = ({ products, title }: ProductCardListProps) => {
  const { t, i18n } = useTranslation("icth-retail-main");
  const [selectedType, setSelectedType] = React.useState<string | null>(null);

  React.useEffect(() => {
    setSelectedType(null);
  }, [products, title]);

  const insuranceTypes = React.useMemo(() => {
    const types = products
      .map((p) => p.subtitle_insurancetype)
      .filter((type): type is string => !!type);
    return Array.from(new Set(types));
  }, [products]);

  const filteredProducts = React.useMemo(() => {
    if (!selectedType) return products;
    return products.filter((p) => p.subtitle_insurancetype === selectedType);
  }, [products, selectedType]);

  const currentLocale = (i18n.language || "th").substring(0, 2);

  let cleanTitle = "";
  if (title) {
    if (currentLocale === "th") {
      cleanTitle = title.replace(/\n/g, "").replace(/\s+/g, "");
    } else {
      cleanTitle = title.replace(/\n/g, " ");
    }
  }

  return (
    <MainContainer>
      <ContentWrapper>
        <Headline>
          <Title>{t("productList.title", { category: cleanTitle })}</Title>
          <Subtitle>{t("productList.subtitle")}</Subtitle>
        </Headline>
        <ProductFilter
          insurance_types={insuranceTypes}
          selected_type={selectedType}
          onSelectType={setSelectedType}
        />
        <GridWrapper>
          {filteredProducts.map((product) => (
            <ProductCard key={product.title} {...product} />
          ))}
        </GridWrapper>
      </ContentWrapper>
    </MainContainer>
  );
};

ProductCardList.propTypes = {
  products: val.isRequired,
  title: val,
};

ProductCardList.defaultProps = {
  title: undefined,
};

export default ProductCardList;
